import { AxiosError } from 'axios'
import { History } from 'history'

import { checkOrderReadiness } from './ApiService'
import { AnnounceMode } from '../components/ClinAnnounceBar/ClinAnnounceBar'
import { createToastAnnounceEvent } from '../events/AnnounceEvent'
import { IAnnouncementAction } from '../types/IAnnouncement'
import { getOpaLinkErrorAnnouncement } from '../pages/PatientAccessForm/PatientAccessForm.utils'

const ACCESS_FORM_PATH =
  /\/programs\/access-programs\/(\d+)\/patient-access-form\/(\d+)/

export interface IAccessFormTarget {
  pathname: string
  state?: {
    patientNumber?: string | null
    patientId?: number | null
    endpoint?: string
    [key: string]: unknown
  }
}

export interface IAccessFormCheckOptions {
  /** Called with true while the readiness check runs and false when it ends: drives the button spinner. */
  onCheckingChange?: (isChecking: boolean) => void
  /** A link to add to the refusal, chosen from the BFF's refusal reason (e.g. PtaScreeningIncomplete). */
  refusalAction?: (reason?: string) => IAnnouncementAction | undefined
}

// One start at a time: a second click while the check runs does nothing.
let isCheckInFlight = false

interface IOrderReadinessTarget {
  programId: string | number
  physicianId: string | number
  patientNumber: string
  patientId?: number | null
}

/**
 * The order-start check shared by every button that starts or continues an order:
 * resolves true when the BFF confirms the order may go ahead; otherwise shows the
 * refusal (with a link chosen from its reason) and resolves false.
 */
const isReadyToOrder = async (
  dispatch: (action: ReturnType<typeof createToastAnnounceEvent>) => void,
  target: IOrderReadinessTarget,
  options: IAccessFormCheckOptions
): Promise<boolean> => {
  if (isCheckInFlight) {
    return false
  }

  isCheckInFlight = true
  options.onCheckingChange?.(true)
  try {
    await checkOrderReadiness(
      String(target.programId),
      String(target.physicianId),
      target.patientNumber,
      target.patientId ?? undefined
    )
    return true
  } catch (error) {
    const { title, description } = getOpaLinkErrorAnnouncement(
      error as AxiosError
    )
    const reason = (error as AxiosError<{ reason?: string }>).response?.data
      ?.reason
    dispatch(
      createToastAnnounceEvent(
        AnnounceMode.Error,
        description,
        title,
        options.refusalAction?.(reason)
      )
    )
    return false
  } finally {
    isCheckInFlight = false
    options.onCheckingChange?.(false)
  }
}

/**
 * CLOS-14590: opens the patient access form only once the BFF confirms the order
 * may be started (PTA consent and screening, same checks as getOPALink), so a
 * refused order keeps the user on the page with the reason instead of opening an
 * empty form. Read-only forms (endpoint) and new-patient forms (no patient
 * number) open as before. getOPALink still checks when the form opens.
 *
 * Resolves true when it navigated, false when the order was refused.
 */
export const pushAccessFormWhenReady = async (
  history: History,
  dispatch: (action: ReturnType<typeof createToastAnnounceEvent>) => void,
  target: IAccessFormTarget,
  options: IAccessFormCheckOptions = {}
): Promise<boolean> => {
  const match = ACCESS_FORM_PATH.exec(target.pathname)
  const patientNumber = target.state?.patientNumber

  if (!match || target.state?.endpoint || !patientNumber) {
    history.push(target)
    return true
  }

  const isReady = await isReadyToOrder(
    dispatch,
    {
      programId: match[1],
      physicianId: match[2],
      patientNumber,
      patientId: target.state?.patientId
    },
    options
  )
  if (isReady) {
    history.push(target)
  }
  return isReady
}

export interface ICheckoutTarget {
  pathname: string
  state: {
    programId?: string | number
    physicianId?: string | number
    patientNumber?: string | null
    patientId?: number | null
    [key: string]: unknown
  }
}

/**
 * CLOS-14565: One more step to go opens checkout only once the BFF confirms the
 * order may go ahead, like Begin order. Otherwise checkout opened, refused Place
 * order, and the suggested Save & close sent the user back, which read as a
 * redirect. Without the details to check, checkout opens and checks itself.
 *
 * Resolves true when it navigated, false when the order was refused.
 */
export const pushCheckoutWhenReady = async (
  history: History,
  dispatch: (action: ReturnType<typeof createToastAnnounceEvent>) => void,
  target: ICheckoutTarget,
  options: IAccessFormCheckOptions = {}
): Promise<boolean> => {
  const { programId, physicianId, patientNumber, patientId } = target.state

  if (!programId || !physicianId || !patientNumber) {
    history.push(target.pathname, target.state)
    return true
  }

  const isReady = await isReadyToOrder(
    dispatch,
    { programId, physicianId, patientNumber, patientId },
    options
  )
  if (isReady) {
    history.push(target.pathname, target.state)
  }
  return isReady
}
